import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import ClientCombobox from '@/components/ClientCombobox';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { Sparkles, Plus, Edit3, Trash2, Search, CalendarDays, FileSpreadsheet, Loader2, Lock, FileSignature, Mail, Phone, Globe, X, ExternalLink, Repeat2 } from 'lucide-react';
import { toast } from 'sonner';
import ProposalsTab from '@/components/ProposalsTab';

const STATUSES = ['New', 'Contacted', 'Qualified', 'Proposal', 'Won', 'Lost'];
// Fallback list used only until the /leads/meta call finishes — the canonical list of
// sources is served by the backend so we don't drift out of sync with LEAD_SOURCES.
const FALLBACK_SOURCES = ['Referral', 'Website', 'Cold Call', 'Social Media', 'Walk-in', 'Event', 'SHINE WEBSITE', 'Other'];
const STATUS_COLOR = {
  New: 'border-amber-500/40 text-amber-200',
  Contacted: 'border-sky-500/40 text-sky-300',
  Qualified: 'border-purple-500/40 text-purple-300',
  Proposal: 'border-cyan-500/40 text-cyan-300',
  Won: 'border-emerald-500/40 text-emerald-300',
  Lost: 'border-red-500/40 text-red-300',
};

const EMPTY_FORM = {
  name: '', company: '', contact: '', email: '',
  source: 'Other', status: 'New', value: 0, notes: '',
  service: '', client_id: '',
};

export default function LeadsPage() {
  const { user, has } = useAuth();
  // Driven by `effective_access` — a user granted `leads_create`/`leads_edit`
  // without the Sales role can now use this page. Fixes the companion to the
  // "Add client" bug.
  const canCreate = has('leads_create');
  const canEditLeads = has('leads_edit');
  const canDeleteLeads = has('leads_delete');
  const canEdit = canCreate || canEditLeads || canDeleteLeads;
  const canExport = user?.role === 'Owner' || user?.role === 'Admin';
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [sources, setSources] = useState(FALLBACK_SOURCES);
  const [filters, setFilters] = useState({
    q: '',
    status: 'all',
    source: 'all',
    service: 'all',
    date_from: '',
    date_to: '',
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  // Live duplicate-check state — populated by the debounced watcher below.
  // Only surfaces suggestions while creating a NEW lead (skip during edit).
  const [dupResult, setDupResult] = useState({ duplicate_leads: [], existing_client: null });
  const [dupLoading, setDupLoading] = useState(false);
  const [dupDismissed, setDupDismissed] = useState(false);
  // Proposals-tab dialog for a given lead.
  const [proposalsLead, setProposalsLead] = useState(null);
  const [detailLead, setDetailLead] = useState(null);
  const canSeeProposals = has('proposals_view');

  const load = async () => {
    const res = await api.get('/leads');
    setItems(res.data);
  };
  useEffect(() => {
    load();
    api.get('/clients').then((r) => setClients(r.data)).catch(() => {});
    api.get('/leads/meta').then((r) => {
      setServices(r.data?.services || []);
      if (Array.isArray(r.data?.sources) && r.data.sources.length) setSources(r.data.sources);
    }).catch(() => {});
  }, []);

  // ---------- Live duplicate-check (debounced) ----------
  // Fires only while the New-Lead dialog is open and NOT editing an existing lead.
  // Debounced ~400ms so we don't hammer the API on every keystroke.
  // Backend already ignores name matches shorter than 3 chars.
  useEffect(() => {
    if (!open || editing) {
      setDupResult({ duplicate_leads: [], existing_client: null });
      return undefined;
    }
    // If user already picked an existing client, the flow is a re-enquiry —
    // duplicates are expected, don't clutter the UI.
    if (form.client_id) {
      setDupResult({ duplicate_leads: [], existing_client: null });
      return undefined;
    }
    const contact = (form.contact || '').trim();
    const company = (form.company || '').trim();
    const name = (form.name || '').trim();
    if (!contact && !company && name.length < 3) {
      setDupResult({ duplicate_leads: [], existing_client: null });
      return undefined;
    }
    setDupLoading(true);
    const handle = setTimeout(async () => {
      try {
        const params = {};
        if (contact) params.contact = contact;
        if (company) params.company = company;
        if (name) params.name = name;
        const r = await api.get('/leads/check-duplicate', { params });
        setDupResult(r.data || { duplicate_leads: [], existing_client: null });
      } catch (_e) {
        setDupResult({ duplicate_leads: [], existing_client: null });
      } finally {
        setDupLoading(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [open, editing, form.contact, form.company, form.name, form.client_id]);

  const filtered = items.filter((l) => {
    if (filters.status !== 'all' && l.status !== filters.status) return false;
    if (filters.source !== 'all' && (l.source || '') !== filters.source) return false;
    if (filters.service !== 'all' && (l.service || '') !== filters.service) return false;
    if (filters.date_from && (l.created_at || '').slice(0, 10) < filters.date_from) return false;
    if (filters.date_to && (l.created_at || '').slice(0, 10) > filters.date_to) return false;
    if (filters.q) {
      const q = filters.q.toLowerCase();
      const hay = `${l.name || ''} ${l.company || ''} ${l.contact || ''} ${l.email || ''} ${l.notes || ''} ${l.service || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const hasActiveFilters = filters.q || filters.status !== 'all' || filters.source !== 'all' || filters.service !== 'all' || filters.date_from || filters.date_to;
  const clearFilters = () => setFilters({ q: '', status: 'all', source: 'all', service: 'all', date_from: '', date_to: '' });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDupDismissed(false);
    setDupResult({ duplicate_leads: [], existing_client: null });
    setOpen(true);
  };

  // Owner/Admin-only Excel export honouring the current filter bar. We pass only
  // the server-supported filters (date range, status, source, service); the free-text
  // `q` is client-side, so the export uses the same criteria that appear in the URL.
  const exportExcel = async () => {
    if (!canExport) return;
    setExporting(true);
    try {
      const params = {};
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.source !== 'all') params.source = filters.source;
      if (filters.service !== 'all') params.service = filters.service;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      // Reuse the shared axios instance so the JWT is attached automatically
      // (it lives under the `shine_token` localStorage key, not `token`).
      const res = await api.get('/leads/export', { params, responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = (filters.date_from || filters.date_to) ? `_${filters.date_from || 'start'}_to_${filters.date_to || 'today'}` : '';
      a.download = `shine_leads${suffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Leads exported.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };
  const openEdit = (l) => {
    setEditing(l);
    setForm({
      name: l.name || '', company: l.company || '', contact: l.contact || '', email: l.email || '',
      source: l.source || 'Other', status: l.status || 'New', value: l.value || 0, notes: l.notes || '',
      service: l.service || '', client_id: l.client_id || '',
    });
    setOpen(true);
  };

  const onPickExistingClient = (cid, cObj) => {
    if (cObj) {
      setForm((f) => ({
        ...f,
        client_id: cid,
        // auto-fill — but don't overwrite if user already typed something
        company: f.company || cObj.company || '',
        contact: f.contact || cObj.phone || '',
        email: f.email || cObj.email || '',
        name: f.name || cObj.name || '',
      }));
    } else {
      setForm((f) => ({ ...f, client_id: '' }));
    }
  };

  const save = async (confirmDuplicate = false) => {
    const payload = { ...form, value: parseFloat(form.value) || 0 };
    if (!payload.client_id) delete payload.client_id;
    if (!payload.service) delete payload.service;
    // confirmDuplicate is only set to `true` by the explicit "Create anyway"
    // retry path. When save() is used as an onClick handler, React passes the
    // event object as the first arg — coerce anything non-boolean to false so
    // we don't accidentally skip the server-side duplicate check.
    if (confirmDuplicate === true) payload.confirm_duplicate = true;
    try {
      if (editing) {
        const r = await api.patch(`/leads/${editing.id}`, payload);
        if (r.data?.status === 'Won' && r.data?.client_id && editing.status !== 'Won') {
          toast.success('Lead won! Client added to the Clients list.');
        } else {
          toast.success('Lead updated');
        }
      } else {
        const r = await api.post('/leads', payload);
        if (r.data?.status === 'Won' && r.data?.client_id) {
          toast.success('Lead created and client added to Clients list.');
        } else {
          toast.success('Lead created');
        }
      }
      setOpen(false);
      load();
      api.get('/clients').then((r) => setClients(r.data)).catch(() => {});
    } catch (e) {
      // Server-side 409 means the backend spotted a duplicate — surface details
      // and offer "Create anyway".
      if (e?.response?.status === 409) {
        const info = e.response.data?.detail || {};
        const dupLeads = info.duplicate_leads || [];
        const existing = info.existing_client;
        let msg = 'Possible duplicate found:\n\n';
        if (existing) msg += `• Already a client — "${existing.company}" (${existing.phone || existing.email || ''}).\n`;
        dupLeads.forEach((d) => { msg += `• Lead "${d.name || d.company}" (${d.status}) owned by ${d.owner_name || 'unknown'}.\n`; });
        msg += '\nCreate anyway?';
        if (window.confirm(msg)) return save(true);
        return;
      }
      toast.error(e?.response?.data?.detail || 'Could not save lead');
    }
  };

  const remove = async (l) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete lead ${l.name}?`)) return;
    await api.delete(`/leads/${l.id}`);
    load();
  };

  const updateStatus = async (l, status) => {
    try {
      const r = await api.patch(`/leads/${l.id}`, { status });
      if (status === 'Won' && r.data?.client_id && l.status !== 'Won') {
        toast.success(`'${r.data.company || l.name}' added to Clients list.`);
      }
      load();
      api.get('/clients').then((rr) => setClients(rr.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update status');
    }
  };

  return (
    <div>
      <PageHeader
        icon={Sparkles}
        title="Leads"
        subtitle={canEdit ? 'Capture and convert new opportunities.' : 'View-only — only Sales can edit leads.'}
        actions={
          <div className="flex items-center gap-2">
            {canExport && (
              <Button
                onClick={exportExcel}
                disabled={exporting}
                variant="outline"
                className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
                data-testid="export-leads-btn"
                title="Export leads to Excel (honours current filters)"
              >
                {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
                Export
              </Button>
            )}
            {canEdit && (
              <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold" data-testid="new-lead-btn">
                <Plus className="h-4 w-4 mr-1" /> New lead
              </Button>
            )}
          </div>
        }
      />

      <Card className="shine-card p-3 rounded-xl mb-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search name, company, phone, email, notes…"
              className="pl-8 bg-[#0f2240] border-amber-500/20 h-9"
              data-testid="lead-search"
            />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="w-32 h-9 bg-[#0f2240] border-amber-500/20" data-testid="lead-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className="bg-[#0a1628] border-amber-500/20">
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
            <SelectTrigger className="w-40 h-9 bg-[#0f2240] border-amber-500/20" data-testid="lead-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent className="bg-[#0a1628] border-amber-500/20 max-h-72">
              <SelectItem value="all">All sources</SelectItem>
              {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.service} onValueChange={(v) => setFilters({ ...filters, service: v })}>
            <SelectTrigger className="w-48 h-9 bg-[#0f2240] border-amber-500/20" data-testid="lead-filter-service"><SelectValue placeholder="Service" /></SelectTrigger>
            <SelectContent className="bg-[#0a1628] border-amber-500/20 max-h-72">
              <SelectItem value="all">All services</SelectItem>
              {services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Label className="text-xs text-slate-400">Date entered:</Label>
          <Input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="h-9 w-40 bg-[#0f2240] border-amber-500/20"
            data-testid="lead-filter-date-from"
          />
          <span className="text-slate-400 text-xs">to</span>
          <Input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="h-9 w-40 bg-[#0f2240] border-amber-500/20"
            data-testid="lead-filter-date-to"
          />
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-400">{filtered.length} of {items.length}</span>
            {hasActiveFilters && (
              <Button
                size="sm"
                variant="ghost"
                onClick={clearFilters}
                className="text-slate-400 hover:text-red-300 h-9"
                data-testid="lead-filter-clear"
              >
                Clear filters
              </Button>
            )}
          </div>
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">No leads.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((l) => (
            <Card key={l.id} className="shine-card p-4 rounded-xl cursor-pointer hover:border-amber-500/40 transition" data-testid={`lead-card-${l.id}`}
                  onClick={() => setDetailLead(l)}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    {l.name}
                    {(l.submission_count || 0) > 1 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-sky-500/10 border border-sky-500/30 text-sky-300 rounded px-1.5 py-0.5"
                            data-testid={`lead-subcount-${l.id}`}>
                        <Repeat2 className="h-3 w-3" /> {l.submission_count} submissions
                      </span>
                    )}
                  </h3>
                  {l.company && <div className="text-xs text-amber-300 mt-0.5">{l.company}</div>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Badge variant="outline" className={STATUS_COLOR[l.status]}>{l.status}</Badge>
                  {l.created_at && (
                    <div className="text-[10px] text-slate-400 flex items-center gap-1" title={new Date(l.created_at).toLocaleString('en-IN')}>
                      <CalendarDays className="h-3 w-3" />
                      {new Date(l.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-400 space-y-1">
                {l.contact && (
                  <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-emerald-400" /> {l.contact}</div>
                )}
                {l.email && (
                  <div className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 text-sky-400" /> <span className="truncate">{l.email}</span></div>
                )}
                <div>Source: {l.source}</div>
                {l.country && (
                  <div className="flex items-center gap-1.5"><Globe className="h-3 w-3 text-indigo-400" /> {l.country}</div>
                )}
                {l.service && (
                  <div className="pt-1">
                    <span className="inline-block text-[10px] uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded px-2 py-0.5">
                      {l.service}
                    </span>
                  </div>
                )}
                {l.client_id && (
                  <div className="pt-1 text-[11px] text-sky-300">↻ Existing client</div>
                )}
              </div>
              {l.money_hidden ? (
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-slate-500/10 text-slate-400 border border-slate-400/20" title="Lead value is restricted by your access settings">
                  <Lock className="h-3 w-3" /> Value restricted
                </div>
              ) : (l.value > 0 && (
                <div className="mt-2 text-sm shine-gold-text font-bold">₹{l.value.toLocaleString('en-IN')}</div>
              ))}
              {l.notes && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{l.notes}</p>}
              {canEdit && (
                <div className="mt-3 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <Select value={l.status} onValueChange={(v) => updateStatus(l, v)}>
                    <SelectTrigger className="h-8 text-xs bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a1628] border-amber-500/20">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(l)} className="text-amber-300 hover:bg-amber-500/10 h-8 w-8"><Edit3 className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(l)} className="text-red-400 hover:bg-red-500/10 h-8 w-8"><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
              {canSeeProposals && (
                <div className="mt-2 pt-2 border-t border-amber-500/10" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" onClick={() => setProposalsLead(l)}
                          className="text-sky-300 hover:bg-sky-500/10 h-8 w-full justify-start"
                          data-testid={`lead-proposals-${l.id}`}>
                    <FileSignature className="h-4 w-4 mr-1" /> Proposals
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editing ? 'Edit lead' : 'New lead'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Existing client picker (re-enquiries) */}
            <div>
              <Label>Existing client (optional)</Label>
              <ClientCombobox
                clients={clients}
                value={form.client_id}
                onChange={onPickExistingClient}
                placeholder="Search existing clients for re-enquiry…"
                testIdPrefix="lead-existing-client"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Pick if this is a repeat enquiry from a current client — name & phone will auto-fill.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="lead-name-input" /></div>
              <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="lead-company-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="lead-contact-input" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
            </div>

            {/* Live duplicate suggestions — only for NEW leads, hides once user dismisses or picks a client */}
            {!editing && !dupDismissed && (dupResult.existing_client || (dupResult.duplicate_leads && dupResult.duplicate_leads.length > 0)) && (
              <div
                className="rounded-lg border border-amber-400/40 bg-amber-500/5 px-3 py-2 space-y-1.5"
                data-testid="lead-duplicate-panel"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-200 flex items-center gap-1.5">
                    <Search className="h-3 w-3" />
                    Possible duplicates
                    {dupLoading && <Loader2 className="h-3 w-3 animate-spin opacity-70" />}
                  </div>
                  <button
                    type="button"
                    onClick={() => setDupDismissed(true)}
                    className="text-[11px] text-slate-400 hover:text-red-300"
                    data-testid="lead-duplicate-dismiss"
                    title="Dismiss suggestions"
                  >
                    Dismiss
                  </button>
                </div>
                {dupResult.existing_client && (
                  <button
                    type="button"
                    onClick={() => onPickExistingClient(dupResult.existing_client.id, dupResult.existing_client)}
                    className="w-full text-left rounded-md bg-[#0f2240] border border-emerald-500/30 hover:border-emerald-500/60 px-2.5 py-1.5 focus:outline-none"
                    data-testid={`lead-duplicate-client-${dupResult.existing_client.id}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-emerald-300">Existing client · click to link (re-enquiry)</div>
                    <div className="text-sm text-white font-semibold truncate">
                      {dupResult.existing_client.company}
                      {dupResult.existing_client.name && <span className="text-slate-400 font-normal"> · {dupResult.existing_client.name}</span>}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {dupResult.existing_client.phone || '—'} · {dupResult.existing_client.email || '—'}
                    </div>
                  </button>
                )}
                {(dupResult.duplicate_leads || []).slice(0, 5).map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => {
                      const match = items.find((it) => it.id === d.id);
                      if (match) {
                        setOpen(false);
                        setTimeout(() => openEdit(match), 50);
                      }
                    }}
                    className="w-full text-left rounded-md bg-[#0f2240] border border-amber-500/20 hover:border-amber-500/50 px-2.5 py-1.5 focus:outline-none"
                    data-testid={`lead-duplicate-lead-${d.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white font-semibold truncate">
                          {d.name || '—'}
                          {d.company && <span className="text-slate-400 font-normal"> · {d.company}</span>}
                        </div>
                        <div className="text-[11px] text-slate-400 truncate">
                          {d.contact || '—'} · {d.email || '—'}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Badge variant="outline" className={`${STATUS_COLOR[d.status] || 'border-slate-500/40 text-slate-300'} text-[10px]`}>
                          {d.status || 'Lead'}
                        </Badge>
                        {Array.isArray(d.match_reasons) && d.match_reasons.length > 0 && (
                          <span className="text-[9px] uppercase tracking-wider text-amber-300/80">
                            match: {d.match_reasons.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    {d.owner_name && (
                      <div className="text-[10px] text-slate-500 mt-0.5">owned by {d.owner_name}</div>
                    )}
                  </button>
                ))}
                <p className="text-[10px] text-slate-500 pt-0.5">
                  Click a row to open the existing entry, or keep typing to refine — you can still submit anyway.
                </p>
              </div>
            )}

            {/* Service enquired about */}
            <div>
              <Label>Service enquired about</Label>
              <Select value={form.service || '_none'} onValueChange={(v) => setForm({ ...form, service: v === '_none' ? '' : v })}>
                <SelectTrigger className="bg-[#0f2240] border-amber-500/20" data-testid="lead-service-select">
                  <SelectValue placeholder="Pick a service" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a1628] border-amber-500/20 max-h-72">
                  <SelectItem value="_none">— None —</SelectItem>
                  {services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a1628] border-amber-500/20">{sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-[#0f2240] border-amber-500/20" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a1628] border-amber-500/20">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Value (₹)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>

            {form.status === 'Won' && (
              <div className="text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 rounded-lg px-3 py-2">
                Saving this as <strong>Won</strong> will automatically add the client to the Clients list (if not already linked).
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={!form.name} data-testid="lead-save-btn" className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Proposals dialog — reuses the shared ProposalsTab component
          so the lead view is byte-for-byte identical with the client-profile tab. */}
      <Dialog open={!!proposalsLead} onOpenChange={(v) => !v && setProposalsLead(null)}>
        <DialogContent className="bg-white text-ink max-w-4xl max-h-[85vh] overflow-y-auto"
                       data-testid="lead-proposals-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-ink">
              <FileSignature className="h-5 w-5 text-green-ink" />
              Proposals · {proposalsLead?.name} <span className="text-ink-2">({proposalsLead?.company})</span>
            </DialogTitle>
          </DialogHeader>
          {proposalsLead && <ProposalsTab leadId={proposalsLead.id} title={null} dense />}
        </DialogContent>
      </Dialog>

      <LeadDetailDialog lead={detailLead} onClose={() => setDetailLead(null)} />
    </div>
  );
}

/* ─────────── Lead detail dialog ─────────── */
function LeadDetailDialog({ lead, onClose }) {
  const { user } = useAuth();
  const isOwnerAdmin = user?.role === 'Owner' || user?.role === 'Admin';
  const [raw, setRaw] = useState(null);
  const [loadingRaw, setLoadingRaw] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    if (!lead || !isOwnerAdmin) return;
    setLoadingRaw(true);
    api.get(`/leads/${lead.id}/raw`).then((r) => setRaw(r.data))
       .catch(() => setRaw(null)).finally(() => setLoadingRaw(false));
  }, [lead, isOwnerAdmin]);

  if (!lead) return null;
  const subs = (raw?.submissions || lead.submissions || []);
  const extras = raw?.extras || lead.extras || {};
  const row = (lbl, val) => val ? (
    <div className="flex gap-3 text-sm">
      <div className="text-slate-400 w-32 flex-shrink-0">{lbl}</div>
      <div className="text-white break-words min-w-0 flex-1">{val}</div>
    </div>
  ) : null;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a1628] text-white max-w-3xl max-h-[85vh] overflow-y-auto border-amber-500/20"
                     data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-300">
            {lead.name}
            {(lead.submission_count || 0) > 1 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-sky-500/10 border border-sky-500/30 text-sky-300 rounded px-1.5 py-0.5">
                <Repeat2 className="h-3 w-3" /> {lead.submission_count} submissions
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <section className="space-y-1" data-testid="lead-detail-fields">
            {row('Company', lead.company)}
            {row('Phone', lead.contact)}
            {row('Email', lead.email)}
            {row('Country', lead.country)}
            {row('Service', lead.service)}
            {row('Budget', lead.value > 0 ? `₹${Number(lead.value).toLocaleString('en-IN')}` : null)}
            {row('Source', lead.source)}
            {row('Status', lead.status)}
            {row('Page URL', lead.page_url && (
              <a href={lead.page_url} target="_blank" rel="noreferrer" className="text-sky-300 inline-flex items-center gap-1 hover:underline">
                {lead.page_url} <ExternalLink className="h-3 w-3" />
              </a>
            ))}
            {row('UTM', [lead.utm_source, lead.utm_medium, lead.utm_campaign].filter(Boolean).join(' · '))}
            {row('Submitted', lead.submitted_at && new Date(lead.submitted_at).toLocaleString('en-IN'))}
            {isOwnerAdmin && row('IP', lead.ip || raw?.ip)}
            {isOwnerAdmin && row('User agent', lead.user_agent || raw?.user_agent)}
          </section>

          {lead.notes && (
            <section>
              <div className="text-xs text-slate-400 uppercase mb-1">Full message</div>
              <div className="whitespace-pre-wrap text-white bg-white/5 rounded p-3 border border-amber-500/10"
                   data-testid="lead-detail-full-message">{lead.notes}</div>
            </section>
          )}

          {Object.keys(extras || {}).length > 0 && (
            <section>
              <div className="text-xs text-slate-400 uppercase mb-1">Extra fields (unmapped)</div>
              <div className="space-y-1 bg-white/5 rounded p-3 border border-amber-500/10">
                {Object.entries(extras).map(([k, v]) => (
                  <div key={k} className="flex gap-3 text-xs"><div className="text-slate-400 w-32">{k}</div><div>{String(v)}</div></div>
                ))}
              </div>
            </section>
          )}

          {subs.length > 1 && (
            <section>
              <div className="text-xs text-slate-400 uppercase mb-1">Submissions timeline ({subs.length})</div>
              <ol className="space-y-2">
                {subs.map((s, i) => (
                  <li key={i} className="border-l-2 border-amber-500/30 pl-3 text-xs">
                    <div className="text-slate-300">{s.received_at && new Date(s.received_at).toLocaleString('en-IN')}</div>
                    {s.message && <div className="text-white whitespace-pre-wrap mt-0.5">{s.message}</div>}
                    {s.country && <div className="text-slate-400 mt-0.5">Country: {s.country}</div>}
                    {s.page_url && <div className="text-slate-400">Page: {s.page_url}</div>}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {isOwnerAdmin && (
            <section>
              <button className="text-xs text-amber-300 hover:underline" onClick={() => setShowRaw(!showRaw)}
                      data-testid="lead-detail-raw-toggle">
                {showRaw ? 'Hide raw submission' : 'Show raw submission'}
              </button>
              {showRaw && (
                <pre className="mt-2 bg-black/60 text-emerald-300 text-[11px] p-3 rounded border border-amber-500/10 overflow-x-auto"
                     data-testid="lead-detail-raw-body">
{loadingRaw ? 'Loading…' : JSON.stringify(raw?.raw_payload || {}, null, 2)}
                </pre>
              )}
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
